import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faDownload,
  faEye,
  faCircleNotch,
  faCheck,
  faTriangleExclamation,
  faLocationDot,
  faEnvelope,
  faPhone,
  faGlobe,
} from '@fortawesome/free-solid-svg-icons'
import { faLinkedin, faGithub } from '@fortawesome/free-brands-svg-icons'
import profile from '../../data/profiles/activeProfile'
import './index.scss'

const { skills, experience, education, summary, title: jobTitle, downloadFilename, pdfUrl, pdfPath } = profile.resume

// Firebase Storage URL preferred; local public/ path as fallback
const resumePdf = pdfUrl || (process.env.PUBLIC_URL + pdfPath)
const { name, contact } = profile

const nameParts = name.split(' ')
const firstNames = nameParts.slice(0, -1).join(' ')
const lastName = nameParts[nameParts.length - 1]

// ── Button state configs ────────────────────────────────────────────────────
const DOWNLOAD = {
  idle:    { icon: faDownload,            label: 'Download CV',    mod: '' },
  loading: { icon: faCircleNotch,         label: 'Downloading…',   mod: 'cv-button--loading' },
  success: { icon: faCheck,              label: 'Downloaded!',    mod: 'cv-button--success' },
  error:   { icon: faTriangleExclamation, label: 'Failed — Retry', mod: 'cv-button--error' },
}

const VIEW = {
  idle:    { icon: faEye,                 label: 'View PDF',       mod: '' },
  loading: { icon: faCircleNotch,         label: 'Opening…',       mod: 'cv-button--loading' },
  error:   { icon: faTriangleExclamation, label: 'Failed — Retry', mod: 'cv-button--error' },
}

const SectionTitle = ({ index, children }) => (
  <h3 className="section-title">
    <span className="section-title__index">{index}</span>
    <span className="section-title__text">{children}</span>
    <span className="section-title__rule" />
  </h3>
)

const Resume = () => {
  const [dlState,   setDlState]   = useState('idle')
  const [viewState, setViewState] = useState('idle')

  // ── Download: fetch → blob → named file save ──────────────────────────────
  const handleDownload = async () => {
    if (dlState !== 'idle') return
    setDlState('loading')
    const MIN_MS = 700
    const start  = Date.now()
    try {
      const res = await fetch(resumePdf)
      if (!res.ok) throw new Error('fetch failed')
      const blob = await res.blob()
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = downloadFilename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      // Wait for remainder of minimum display time before showing success
      const elapsed = Date.now() - start
      setTimeout(() => {
        setDlState('success')
        setTimeout(() => setDlState('idle'), 2200)
      }, Math.max(0, MIN_MS - elapsed))
    } catch {
      setDlState('error')
      setTimeout(() => setDlState('idle'), 3000)
    }
  }

  // ── View PDF: open window synchronously → fetch blob → styled iframe viewer
  // Opening synchronously (before any await) prevents popup blockers.
  const handleViewPdf = async () => {
    if (viewState !== 'idle') return
    setViewState('loading')

    const win = window.open('', '_blank')
    if (!win) {
      setViewState('error')
      setTimeout(() => setViewState('idle'), 3000)
      return
    }

    // Loading screen — visible while we fetch the blob
    win.document.write(`<!DOCTYPE html><html><head><title>Loading Resume…</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{background:#07070F;display:flex;flex-direction:column;align-items:center;
          justify-content:center;height:100vh;gap:20px;font-family:'Courier New',monospace;
          color:#E8A838;letter-spacing:2px;font-size:12px;text-transform:uppercase}
        .dots{display:flex;gap:8px}
        .dot{width:10px;height:10px;border-radius:50%;background:#E8A838;
          animation:bounce 1.2s ease-in-out infinite}
        .dot:nth-child(2){animation-delay:.2s}
        .dot:nth-child(3){animation-delay:.4s}
        @keyframes bounce{0%,80%,100%{transform:scale(0.2);opacity:.4}40%{transform:scale(1);opacity:1}}
      </style></head><body>
      <div class="dots">
        <span class="dot"></span><span class="dot"></span><span class="dot"></span>
      </div>
      <span>Loading Resume</span>
    </body></html>`)

    try {
      const res  = await fetch(resumePdf)
      if (!res.ok) throw new Error('fetch failed')
      const blob = await res.blob()
      const url  = URL.createObjectURL(blob)

      // Replace loading screen with branded PDF viewer
      win.document.open()
      win.document.write(`<!DOCTYPE html><html>
        <head>
          <title>Vamsi Krishna — Resume</title>
          <style>
            *{margin:0;padding:0;box-sizing:border-box}
            body{
              background:#07070F;
              display:flex;
              flex-direction:column;
              height:100vh;
              font-family:'Courier New',monospace;
              overflow:hidden;
            }
            .viewer-header{
              background:#07070F;
              border-bottom:1px solid rgba(232,168,56,0.2);
              padding:13px 24px;
              display:flex;
              align-items:center;
              justify-content:space-between;
              flex-shrink:0;
            }
            .viewer-title{
              color:#E8A838;
              font-size:11px;
              letter-spacing:2.5px;
              text-transform:uppercase;
              font-weight:700;
            }
            .viewer-subtitle{
              color:rgba(232,168,56,0.45);
              font-size:10px;
              letter-spacing:1.5px;
              text-transform:uppercase;
              margin-top:3px;
            }
            .viewer-download{
              display:inline-flex;
              align-items:center;
              gap:8px;
              background:#E8A838;
              color:#0A0A0D;
              border:none;
              padding:9px 20px;
              font-size:10px;
              font-weight:700;
              letter-spacing:2.5px;
              text-transform:uppercase;
              cursor:pointer;
              font-family:'Courier New',monospace;
              text-decoration:none;
              transition:background .2s,color .2s;
            }
            .viewer-download:hover{background:transparent;color:#E8A838;outline:1px solid #E8A838}
            iframe{
              flex:1;
              border:none;
              background:#f0f0f0;
            }
          </style>
        </head>
        <body>
          <div class="viewer-header">
            <div>
              <div class="viewer-title">Vamsi Krishna Chiguruwada</div>
              <div class="viewer-subtitle">Senior Frontend Developer</div>
            </div>
            <a href="${url}" download="${downloadFilename}" class="viewer-download">
              ↓ &nbsp;Download
            </a>
          </div>
          <iframe src="${url}" title="Vamsi Krishna Resume"></iframe>
        </body>
      </html>`)
      win.document.close()
      setViewState('idle')
    } catch {
      win.close()
      setViewState('error')
      setTimeout(() => setViewState('idle'), 3000)
    }
  }

  const dl   = DOWNLOAD[dlState]
  const view = VIEW[viewState]

  return (
    <div className="resume-page">
      <div className="resume-container">

        {/* ── Editorial hero ── */}
        <header className="resume-hero">
          <div className="resume-hero__main">
            <p className="resume-kicker">
              <span className="resume-kicker__rule" />
              Curriculum Vitae
            </p>
            <h1 className="resume-name">
              {firstNames} <em>{lastName}</em>
            </h1>
            <p className="resume-role">
              <span className="resume-role__prompt">&gt;</span> {jobTitle}
            </p>
          </div>

          <div className="resume-actions">
            <button
              onClick={handleDownload}
              className={`cv-button cv-button--primary ${dl.mod}`}
              disabled={dlState !== 'idle'}
              aria-label="Download resume as PDF"
              data-cursor="save"
            >
              <FontAwesomeIcon icon={dl.icon} className={dlState === 'loading' ? 'cv-spin' : ''} />
              <span>{dl.label}</span>
            </button>

            <button
              onClick={handleViewPdf}
              className={`cv-button cv-button--outline ${view.mod}`}
              disabled={viewState !== 'idle'}
              aria-label="View resume PDF in new tab"
              data-cursor="read"
            >
              <FontAwesomeIcon icon={view.icon} className={viewState === 'loading' ? 'cv-spin' : ''} />
              <span>{view.label}</span>
            </button>
          </div>
        </header>

        {/* ── Contact rail ── */}
        <div className="resume-contact-row">
          <a href={`mailto:${contact.email}`} className="contact-item">
            <FontAwesomeIcon icon={faEnvelope} />
            <span>{contact.email}</span>
          </a>
          <a href={`tel:${contact.phone.replace(/\D/g, '').replace(/^/, '+')}`} className="contact-item">
            <FontAwesomeIcon icon={faPhone} />
            <span>{contact.phone}</span>
          </a>
          <span className="contact-item">
            <FontAwesomeIcon icon={faLocationDot} />
            <span>{contact.location.replace(' — USA', '')}</span>
          </span>
          <a href={contact.linkedin.url} target="_blank" rel="noreferrer" className="contact-item">
            <FontAwesomeIcon icon={faLinkedin} />
            <span>{contact.linkedin.label}</span>
          </a>
          <a href={contact.github.url} target="_blank" rel="noreferrer" className="contact-item">
            <FontAwesomeIcon icon={faGithub} />
            <span>{contact.github.label}</span>
          </a>
          <a href={contact.website} target="_blank" rel="noreferrer" className="contact-item">
            <FontAwesomeIcon icon={faGlobe} />
            <span>{contact.website.replace('https://', '')}</span>
          </a>
        </div>

        {/* ── Body ── */}
        <div className="resume-body">

          {/* Main column */}
          <main className="resume-main">

            <section className="resume-section">
              <SectionTitle index="01">Summary</SectionTitle>
              <p className="summary-text">{summary}</p>
            </section>

            <section className="resume-section">
              <SectionTitle index="02">Experience</SectionTitle>
              <div className="experience-list">
                {experience.map((job, i) => (
                  <article key={i} className="job-entry">
                    <div className="job-header">
                      <div>
                        <p className="job-company">{job.company}</p>
                        <p className="job-title">{job.title}</p>
                      </div>
                      <span className="job-range">{job.range}</span>
                    </div>
                    <ul className="job-bullets">
                      {job.bullets.map((b, j) => (
                        <li key={j}>
                          {typeof b === 'object' ? (
                            <>
                              <strong className="resume-bullet-lead">{b.lead}</strong>
                              {' — '}
                              <span>{b.detail}</span>
                            </>
                          ) : b}
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </section>

          </main>

          {/* Side rail */}
          <aside className="resume-side">

            <section className="resume-section">
              <SectionTitle index="03">Skills</SectionTitle>
              <div className="skills-list">
                {skills.map((cat, i) => (
                  <div key={i} className="skill-group">
                    <span className="skill-category-label">{cat.category}</span>
                    <div className="skill-tag-row">
                      {cat.items.map((item, j) => (
                        <span key={j} className="skill-chip">{item}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="resume-section">
              <SectionTitle index="04">Education</SectionTitle>
              {education.map((edu, i) => (
                <div key={i} className="edu-card">
                  <p className="edu-school">{edu.school}</p>
                  <p className="edu-degree">{edu.degree}</p>
                  <p className="edu-meta">{edu.location} · {edu.range}</p>
                </div>
              ))}
            </section>

          </aside>
        </div>
      </div>
    </div>
  )
}

export default Resume
